import { IPublicClientApplication, AccountInfo } from "@azure/msal-browser";
import { CompanyId, COMPANIES } from "../Config/CompanyConfig";

export interface EntraUserProfile {
  id?: string;
  displayName?: string;
  givenName?: string;
  surname?: string;
  mail?: string;
  userPrincipalName?: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  mobilePhone?: string | null;
  businessPhones?: string[];
  companyName?: string;
  employeeId?: string;
  city?: string | null;
  country?: string | null;
  [key: string]: any;
}

/**
 * Dynamically resolves the company's official website based on companyName.
 * Easily extensible for new companies or subsidiaries.
 */
export const getCompanyWebsite = (companyName?: string): string => {
  if (!companyName) return "https://ekopowerlimited.com";
  const cleaned = companyName.trim().toLowerCase();

  // Dynamic directory of known companies and their corresponding official portals
  const companyDirectory: Record<string, string> = {
    "eko electricity distribution company": "https://ekedp.com",
    "eko electricity distribution plc": "https://ekedp.com",
    "ekedc": "https://ekedp.com",
    "ekedp": "https://ekedp.com",
    "eko power": "https://ekopowerlimited.com",
    "eko power limited": "https://ekopowerlimited.com",
  };

  for (const [key, url] of Object.entries(companyDirectory)) {
    if (cleaned.includes(key) || key.includes(cleaned)) {
      return url;
    }
  }

  // Fallback default
  return "https://ekopowerlimited.com";
};

/**
 * Safely parses mobile and business phones from Microsoft Entra ID.
 * Handles single numbers, comma-separated lists, and phone arrays.
 */
export const parseEntraPhones = (
  businessPhones?: string[],
  mobilePhone?: string | null
): { officialPhone: string; personalTel: string } => {
  const numbers: string[] = [];

  if (Array.isArray(businessPhones)) {
    businessPhones.forEach((p) => {
      if (typeof p === "string") {
        p.split(",").forEach((item) => {
          const trimmed = item.trim();
          if (trimmed && !numbers.includes(trimmed)) {
            numbers.push(trimmed);
          }
        });
      }
    });
  }

  if (mobilePhone && typeof mobilePhone === "string") {
    const trimmed = mobilePhone.trim();
    if (trimmed && !numbers.includes(trimmed)) {
      numbers.push(trimmed);
    }
  }

  return {
    officialPhone: numbers[0] || "",
    personalTel: numbers[1] || "",
  };
};

/**
 * Fetches the logged-in user's profile and token claims from Microsoft Entra ID (Azure AD),
 * and prints detailed, formatted information to the browser console.
 */
export const fetchAndLogEntraUserInfo = async (
  instance: IPublicClientApplication
): Promise<EntraUserProfile | null> => {
  try {
    const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
    if (!account) {
      console.warn("⚠️ [Microsoft Entra ID] No active account found in MSAL cache.");
      return null;
    }

    // 1. Log MSAL Account and ID Token Claims
    console.group("🔑 [Microsoft Entra ID] Account & ID Token Claims");
    console.log("Account Object:", account);
    console.log("ID Token Claims:", account.idTokenClaims);
    console.table({
      "Display Name": account.name || "N/A",
      "Username (UPN)": account.username || "N/A",
      "Object ID (oid)": account.localAccountId || "N/A",
      "Tenant ID (tid)": account.tenantId || "N/A",
      "Roles": (account.idTokenClaims as any)?.roles?.join(", ") || "None",
    });
    console.groupEnd();

    // 2. Acquire Microsoft Graph Access Token
    const tokenResponse = await instance.acquireTokenSilent({
      scopes: ["User.Read"],
      account,
    });

    // 3. Query Microsoft Graph /v1.0/me for directory profile
    const graphResponse = await fetch(
      "https://graph.microsoft.com/v1.0/me?$select=id,displayName,givenName,surname,mail,userPrincipalName,jobTitle,department,officeLocation,mobilePhone,businessPhones,companyName,employeeId,city,country",
      {
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          Accept: "application/json",
        },
      }
    );

    if (graphResponse.ok) {
      const graphData: EntraUserProfile = await graphResponse.json();

      console.group("🏢 [Microsoft Entra ID] Graph Profile (/v1.0/me)");
      console.log("Full Raw Profile Data:", graphData);
      console.table({
        "Display Name": graphData.displayName || "N/A",
        "First Name": graphData.givenName || "N/A",
        "Last Name": graphData.surname || "N/A",
        "Job Title / Role": graphData.jobTitle || "N/A",
        "Department": graphData.department || "N/A",
        "Office Location": graphData.officeLocation || "N/A",
        "Email (Mail)": graphData.mail || "N/A",
        "User Principal Name": graphData.userPrincipalName || "N/A",
        "Mobile Phone": graphData.mobilePhone || "N/A",
        "Business Phone(s)": graphData.businessPhones?.join(", ") || "N/A",
        "Company Name": graphData.companyName || "N/A",
        "Employee ID": graphData.employeeId || "N/A",
      });
      console.groupEnd();

      return graphData;
    } else {
      console.warn(
        "⚠️ [Microsoft Entra ID] Graph request failed with status:",
        graphResponse.status,
        graphResponse.statusText
      );
      return null;
    }
  } catch (error) {
    console.error("❌ [Microsoft Entra ID] Error fetching user profile:", error);
    return null;
  }
};

/**
 * Result of cross-tenant membership validation
 */
export interface TenantValidationResult {
  isValid: boolean;
  userCompany?: CompanyId | 'unknown';
  reason?: string;
  userGroupIds: string[];
}

/**
 * Queries Microsoft Graph /v1.0/me/memberOf to retrieve all Group / Directory Object IDs
 * that the currently authenticated user belongs to.
 */
export const fetchUserMemberGroupIds = async (
  instance: IPublicClientApplication,
  account: AccountInfo
): Promise<string[]> => {
  try {
    const tokenResponse = await instance.acquireTokenSilent({
      scopes: ["User.Read"],
      account,
    });

    const response = await fetch("https://graph.microsoft.com/v1.0/me/memberOf", {
      headers: {
        Authorization: `Bearer ${tokenResponse.accessToken}`,
        Accept: "application/json",
      },
    });

    if (response.ok) {
      const data = await response.json();
      const groupIds: string[] = (data.value || [])
        .map((item: any) => item.id)
        .filter(Boolean);

      console.group("🔐 [Microsoft Entra ID] /me/memberOf Group Check");
      console.log(`Found ${groupIds.length} membership(s):`, groupIds);
      console.groupEnd();

      return groupIds;
    } else {
      console.warn("⚠️ [Microsoft Entra ID] /me/memberOf failed with status:", response.status, response.statusText);
      return [];
    }
  } catch (error) {
    console.error("❌ [Microsoft Entra ID] Error querying /me/memberOf:", error);
    return [];
  }
};

/**
 * Validates whether the user's group memberships match the active domain/portal.
 * 
 * Rules:
 * - On Eko Power domain: user MUST have Eko Power group (2062e130-258d-4876-ac65-9fdbb2963bf1).
 *   If they belong to EKEDP (e1802ab5-b821-4334-9e84-91b9649b8cf0), they are kicked out.
 * - On EKEDP domain: user MUST have EKEDP group (e1802ab5-b821-4334-9e84-91b9649b8cf0).
 *   If they belong to Eko Power (2062e130-258d-4876-ac65-9fdbb2963bf1), they are kicked out.
 * - Vice versa!
 */
export const validateTenantAccess = (
  currentCompanyId: CompanyId,
  userGroupIds: string[]
): TenantValidationResult => {
  const currentConfig = COMPANIES[currentCompanyId];
  const isEkoPowerMember = userGroupIds.includes(COMPANIES.ekopower.requiredGroupId);
  const isEkedpMember = userGroupIds.includes(COMPANIES.ekedp.requiredGroupId);

  // User has the required group for current active portal
  if (userGroupIds.includes(currentConfig.requiredGroupId)) {
    return {
      isValid: true,
      userCompany: currentCompanyId,
      userGroupIds,
    };
  }

  // Cross-tenant mismatch: User is an EKEDP member trying to access Eko Power
  if (currentCompanyId === 'ekopower' && isEkedpMember) {
    return {
      isValid: false,
      userCompany: 'ekedp',
      userGroupIds,
      reason: 'You are signed in with an EKEDP corporate account on the Eko Power portal. Please access your digital card on the EKEDP portal.',
    };
  }

  // Cross-tenant mismatch: User is an Eko Power member trying to access EKEDP
  if (currentCompanyId === 'ekedp' && isEkoPowerMember) {
    return {
      isValid: false,
      userCompany: 'ekopower',
      userGroupIds,
      reason: 'You are signed in with an Eko Power corporate account on the EKEDP portal. Please access your digital card on the Eko Power portal.',
    };
  }

  // Neither authorized group found
  return {
    isValid: false,
    userCompany: 'unknown',
    userGroupIds,
    reason: `Your account is not authorized to access the ${currentConfig.companyName} portal.`,
  };
};

